"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import Button from "../../../../../components/Button";
import Checkbox from "../../../../../components/Checkbox";
import { Check } from "lucide-react";

import { useConsultation } from "../../../../../context/ConsultationContext";
import { validateIdOrDob, validateInjectionAdministrationConsentForm } from "../../utils/validation";
import SectionCard from "./components/SectionCard";
import TextInputField from "./components/TextInputField";
import TextAreaField from "./components/TextAreaField";
import FormInput from "../../../../../components/FormInput";

import styles from "./css/InjectionAdministration.module.css";

const initialForm = {
  patientFullName: "",
  patientIdOrDob: "",
  patientContactNumber: "",
  dateOfVisit: "",
  patientAddress: "",
  injectionMedicineName: "",
  injectionStrength: "",
  routeOfAdministration: "",
  routeOther: "",
  batchNumber: "",
  expiryDate: "",
  administratorName: "",
  administratorTitleRank: "",
  administratorRegistrationNumber: "",
  administratorSignature: "",
  administratorSignatureDate: "",
  patientSignature: "",
  patientSignatureDate: "",
  voluntaryConsent: false,
  popiaConsent: false,
};

export default function InjectionAdministration() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultationData, setIsInConsultation, getStepDraft, setStepDraft } = useConsultation();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayStr = useMemo(() => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(today.getDate())}-${pad(today.getMonth() + 1)}-${today.getFullYear()}`;
  }, [today]);

  const patient = consultationData?.patient || {};
  const patientName = useMemo(() => {
    const first = String(patient?.firstname || "").trim();
    const last = String(patient?.surname || "").trim();
    const full = `${first} ${last}`.trim();
    return full;
  }, [patient?.firstname, patient?.surname]);

  const [formData, setFormData] = useState(() => ({
    ...initialForm,
    patientFullName: patientName || initialForm.patientFullName,
    dateOfVisit: todayStr,
    administratorSignatureDate: todayStr,
    patientSignatureDate: todayStr,
  }));

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === "patientIdOrDob") {
      const res = validateIdOrDob(value);
      setValidationErrors((prev) => {
        const next = { ...prev };
        if (res.isValid) {
          delete next.patientIdOrDob;
        } else {
          next.patientIdOrDob = res.error;
        }
        return next;
      });
    }
  };

  const setRoute = (route) => {
    setFormData((prev) => ({
      ...prev,
      routeOfAdministration: route,
      routeOther: route === "Other" ? prev.routeOther : "",
    }));
  };

  const focusFirstError = (errors) => {
    const fieldOrder = [
      "patientFullName",
      "patientIdOrDob",
      "patientContactNumber",
      "dateOfVisit",
      "patientAddress",
      "injectionMedicineName",
      "routeOfAdministration",
      "batchNumber",
      "expiryDate",
      "administratorName",
      "administratorTitleRank",
      "administratorRegistrationNumber",
      "administratorSignature",
      "patientSignature",
      "voluntaryConsent",
      "popiaConsent",
    ];

    const field = fieldOrder.find((f) => errors?.[f]);
    if (!field) return;

    const el = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el?.focus) el.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // Validate form
    const errors = validateInjectionAdministrationConsentForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setTimeout(() => focusFirstError(errors), 0);
      return;
    }

    setSaveError("");
    setSaving(true);
    try {
      const payload = {
        ...formData,
        appointmentId: consultationData?.appointmentId ?? null,
        patientId: consultationData?.patientId ?? null,
      };

      // Save to consultation draft context (for DB save on Invoice Finalize)
      const existing = getStepDraft?.("consentForms", {}) || {};
      setStepDraft?.("consentForms", {
        ...existing,
        injection_administration: {
          answers: payload,
          patientSigned: !!formData.patientSignature,
          patientSignedAt: formData.patientSignatureDate ?? null,
          practitionerSigned: !!formData.administratorName,
          practitionerSignedAt: formData.administratorSignatureDate ?? null,
        },
      });

      try {
        sessionStorage.setItem("clinic.consent.injectionAdministration", JSON.stringify(payload));
      } catch {}

      const continueTo = location?.state?.continueTo;
      setIsInConsultation?.(true);
      navigate(typeof continueTo === "string" && continueTo.trim() ? continueTo : "/customer/clinic/patient-overview");
    } catch (err) {
      setSaveError(err?.message || "Failed to save consent form.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <form className={styles.formContainer} onSubmit={handleSubmit}>
            <header className={styles.docHeader}>
              <div className={styles.docHeaderTop}>
                <div className={styles.pharmacyName}>Durban North Pharmacy</div>
                <div className={styles.pharmacyContact}>35 Umhlanga Rocks Drive | Tel: 031 563 7577</div>
              </div>
              <div className={styles.purpleRule} />
              <h1 className={styles.docTitle}>Consent Form - Administration of Injections (Non-Vaccine)</h1>
            </header>

            <SectionCard title="1. Patient Details" styles={styles}>
              <div className={styles.grid2}>
                <TextInputField
                  id="patientFullName"
                  name="patientFullName"
                  label="Full Name"
                  value={formData.patientFullName}
                  onChange={handleInputChange}
                  placeholder="Full name"
                  autoComplete="name"
                  error={validationErrors.patientFullName}
                  styles={styles}
                />

                <TextInputField
                  id="patientIdOrDob"
                  name="patientIdOrDob"
                  label="ID Number / Date of Birth"
                  value={formData.patientIdOrDob}
                  onChange={handleInputChange}
                  placeholder="ID number or DOB"
                  error={validationErrors.patientIdOrDob}
                  styles={styles}
                />

                <TextInputField
                  id="patientContactNumber"
                  name="patientContactNumber"
                  label="Contact Number"
                  value={formData.patientContactNumber}
                  onChange={handleInputChange}
                  placeholder="Contact number"
                  autoComplete="tel"
                  error={validationErrors.patientContactNumber}
                  styles={styles}
                />

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date of Visit"
                    type="text"
                    name="dateOfVisit"
                    value={formData.dateOfVisit}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                  {validationErrors.dateOfVisit && <div className={styles.errorMessage}>{validationErrors.dateOfVisit}</div>}
                </div>
              </div>

              <TextAreaField
                id="patientAddress"
                name="patientAddress"
                label="Address"
                value={formData.patientAddress}
                onChange={handleInputChange}
                placeholder="Address"
                error={validationErrors.patientAddress}
                styles={styles}
              />
            </SectionCard>

            <SectionCard title="2. Injection Details - For Clinic Completion" styles={styles}>
              <div className={styles.grid2}>
                <TextInputField
                  id="injectionMedicineName"
                  name="injectionMedicineName"
                  label="Injection / Medicine Name"
                  value={formData.injectionMedicineName}
                  onChange={handleInputChange}
                  placeholder="Injection / medicine"
                  error={validationErrors.injectionMedicineName}
                  styles={styles}
                />

                <TextInputField
                  id="injectionStrength"
                  name="injectionStrength"
                  label="Strength (if applicable)"
                  value={formData.injectionStrength}
                  onChange={handleInputChange}
                  placeholder="Strength"
                  styles={styles}
                />
              </div>

              <div className={styles.routeRow}>
                <div className={styles.routeLabel}>Route of Administration:</div>
                <label className={styles.radioItem}>
                  <input
                    type="radio"
                    name="routeOfAdministration"
                    checked={formData.routeOfAdministration === "IM"}
                    onChange={() => setRoute("IM")}
                  />
                  IM
                </label>
                <label className={styles.radioItem}>
                  <input
                    type="radio"
                    name="routeOfAdministration"
                    checked={formData.routeOfAdministration === "SC"}
                    onChange={() => setRoute("SC")}
                  />
                  SC
                </label>
                <label className={styles.radioItem}>
                  <input
                    type="radio"
                    name="routeOfAdministration"
                    checked={formData.routeOfAdministration === "Other"}
                    onChange={() => setRoute("Other")}
                  />
                  Other
                </label>
                {formData.routeOfAdministration === "Other" && (
                  <input
                    className={styles.routeOther}
                    name="routeOther"
                    value={formData.routeOther}
                    onChange={handleInputChange}
                    placeholder="Specify"
                    aria-label="Other route of administration"
                  />
                )}
              </div>

              {validationErrors.routeOfAdministration && (
                <div className={styles.errorMessage}>{validationErrors.routeOfAdministration}</div>
              )}

              <div className={styles.grid2}>
                <TextInputField
                  id="batchNumber"
                  name="batchNumber"
                  label="Batch Number"
                  value={formData.batchNumber}
                  onChange={handleInputChange}
                  placeholder="Batch number"
                  error={validationErrors.batchNumber}
                  styles={styles}
                />

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Expiry Date"
                    type="text"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    inputClassName={validationErrors.expiryDate ? styles.inputError : ""}
                  />
                  {validationErrors.expiryDate && <div className={styles.errorMessage}>{validationErrors.expiryDate}</div>}
                </div>
              </div>
            </SectionCard>

            <SectionCard title="3. Nature of Service" styles={styles}>
              <p className={styles.statement}>
                I understand that I am receiving an injection administered by an authorised healthcare professional within their scope of
                practice. The procedure and purpose of the injection have been explained to me in a manner I understand.
              </p>
            </SectionCard>

            <SectionCard title="4. Risks &amp; Possible Side Effects" styles={styles}>
              <p className={styles.statement}>
                I acknowledge that possible side effects may include: pain, redness, swelling, or bruising at the injection site; dizziness or
                fainting; infection (rare); allergic reaction (rare).
              </p>
              <p className={styles.statement}>
                I understand that this service does not replace medical care and that I may be referred to a medical practitioner if clinically
                indicated.
              </p>
            </SectionCard>

            <SectionCard title="5. POPIA - Use of Personal Information" styles={styles}>
              <p className={styles.statement}>
                I consent to the lawful collection, recording, and secure storage of my personal and health information for the purpose of
                providing pharmacy clinic services, in accordance with the Protection of Personal Information Act (POPIA).
              </p>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="popiaConsent"
                  label="I consent to the use of my personal information (POPIA)."
                  checked={formData.popiaConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, popiaConsent: e.target.checked }))}
                />
              </div>
              {validationErrors.popiaConsent && (
                <div className={styles.errorMessage}>{validationErrors.popiaConsent}</div>
              )}
            </SectionCard>

            <SectionCard title="6. Voluntary Consent" styles={styles}>
              <p className={styles.statement}>
                I confirm that the service has been explained to me; I have had the opportunity to ask questions; participation is voluntary
                and I may withdraw consent at any time.
              </p>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="voluntaryConsent"
                  label="I confirm that I provide voluntary consent."
                  checked={formData.voluntaryConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, voluntaryConsent: e.target.checked }))}
                />
              </div>
              {validationErrors.voluntaryConsent && (
                <div className={styles.errorMessage}>{validationErrors.voluntaryConsent}</div>
              )}
            </SectionCard>

            <SectionCard title="7. Administrator Details - For Clinic Completion" styles={styles}>
              <div className={styles.grid2}>
                <TextInputField
                  id="administratorName"
                  name="administratorName"
                  label="Name of Administrator"
                  value={formData.administratorName}
                  onChange={handleInputChange}
                  placeholder="Administrator name"
                  error={validationErrors.administratorName}
                  styles={styles}
                />

                <TextInputField
                  id="administratorTitleRank"
                  name="administratorTitleRank"
                  label="Professional Title / Rank"
                  value={formData.administratorTitleRank}
                  onChange={handleInputChange}
                  placeholder="Title / rank"
                  error={validationErrors.administratorTitleRank}
                  styles={styles}
                />

                <TextInputField
                  id="administratorRegistrationNumber"
                  name="administratorRegistrationNumber"
                  label="Registration Number (if applicable)"
                  value={formData.administratorRegistrationNumber}
                  onChange={handleInputChange}
                  placeholder="Registration number"
                  error={validationErrors.administratorRegistrationNumber}
                  styles={styles}
                />
                <div />
              </div>

              <div className={styles.grid2}>
                <TextInputField
                  id="administratorSignature"
                  name="administratorSignature"
                  label="Administrator Signature"
                  value={formData.administratorSignature}
                  onChange={handleInputChange}
                  placeholder="Type name as signature"
                  error={validationErrors.administratorSignature}
                  styles={styles}
                />

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="administratorSignatureDate"
                    value={formData.administratorSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="8. Patient Consent" styles={styles}>
              <p className={styles.statement}>I hereby voluntarily consent to the administration of the injection as described above.</p>

              <div className={styles.grid2}>
                <TextInputField
                  id="patientSignature"
                  name="patientSignature"
                  label="Patient Signature"
                  value={formData.patientSignature}
                  onChange={handleInputChange}
                  placeholder="Type name as signature"
                  error={validationErrors.patientSignature}
                  styles={styles}
                />

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="patientSignatureDate"
                    value={formData.patientSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                </div>
              </div>
            </SectionCard>

            {saveError && <div className={styles.inlineError}>{saveError}</div>}

            <div className={styles.completeContainer}>
              <Button variant="primary" className={styles.completeButton} disabled={saving} type="submit">
                {saving ? (
                  "Completing..."
                ) : (
                  <>
                    Complete <Check size={22} />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}